import { useState } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

const HARD_MAX_CPS = 20;
const TECH_FLAGS = new Set(["technical_entity_mismatch", "entity_restored_fallback", "entity_mismatch"]);

export function liveCps(seg) {
  const dur = (Number(seg.end) || 0) - (Number(seg.start) || 0);
  if (dur <= 0) return null;
  const chars = (seg.translated_text || "").replace(/\n/g, " ").trim().length;
  return chars / dur;
}

function Chip({ id, label, tone, tip }) {
  const [open, setOpen] = useState(false);
  const tones = {
    orange: "border-orange-500/50 text-orange-300 bg-orange-500/10",
    red: "border-red-500/50 text-red-300 bg-red-500/10",
    yellow: "border-yellow-500/50 text-yellow-300 bg-yellow-500/10",
    purple: "border-purple-500/50 text-purple-300 bg-purple-500/10",
    grey: "border-zinc-700 text-zinc-400 bg-zinc-800/60",
  };
  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-testid={`cue-badge-${id}`}
          onClick={() => setOpen((v) => !v)}
          className={`inline-flex items-center h-4 px-1.5 border rounded-sm text-[9px] font-mono uppercase tracking-wider leading-none ${tones[tone]}`}
        >
          {label}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs bg-zinc-900 text-zinc-100 border border-zinc-700 text-[11px] normal-case tracking-normal">
        {tip}
      </TooltipContent>
    </Tooltip>
  );
}

export function cueHasBadges(seg) {
  const flags = new Set(seg.qc_flags || []);
  const cps = liveCps(seg);
  return (
    flags.has("UNSPLITTABLE_SPAN") ||
    (cps !== null && cps > HARD_MAX_CPS) ||
    flags.has("BOUNDARY_SHIFT") ||
    [...flags].some((f) => TECH_FLAGS.has(f)) ||
    flags.has("outro_midvideo")
  );
}

export function EditorMetricsStrip({ segments, mediaSeconds, flaggedOnly, onToggleFlagged }) {
  const cpsValues = segments.map(liveCps).filter((c) => c !== null && Number.isFinite(c));
  const avg = cpsValues.length ? cpsValues.reduce((a, b) => a + b, 0) / cpsValues.length : 0;
  const max = cpsValues.length ? Math.max(...cpsValues) : 0;
  const lastEnd = segments.length ? Math.max(...segments.map((s) => Number(s.end) || 0)) : 0;
  const marginMs = mediaSeconds > 0 ? Math.round((lastEnd - mediaSeconds) * 1000) : null;
  const flaggedCount = segments.filter(cueHasBadges).length;
  const item = (id, label, value, tone = "text-zinc-200") => (
    <span data-testid={`qa-metric-${id}`} className="inline-flex items-baseline gap-1">
      <span className="text-zinc-500">{label}</span>
      <span className={`${tone} font-medium`}>{value}</span>
    </span>
  );
  return (
    <div
      data-testid="qa-metrics-strip"
      className="flex items-center gap-x-3 gap-y-1 flex-wrap px-3 py-1.5 min-h-8 border-b border-zinc-800 bg-zinc-950/60 text-[10px] font-mono uppercase tracking-wider"
    >
      {item("cues", "Cues", segments.length)}
      {item("avg-cps", "Avg CPS", avg.toFixed(1), avg > HARD_MAX_CPS ? "text-red-300" : "text-zinc-200")}
      {item("max-cps", "Max CPS", max.toFixed(1), max > HARD_MAX_CPS ? "text-red-300" : "text-zinc-200")}
      {item("end-margin", "End margin", marginMs === null ? "—" : `${marginMs > 0 ? "+" : ""}${marginMs} ms`,
        marginMs !== null && marginMs > -50 ? "text-red-300" : "text-zinc-200")}
      <label className="ml-auto inline-flex items-center gap-1.5 cursor-pointer select-none text-zinc-400 hover:text-zinc-200">
        <input
          type="checkbox"
          data-testid="flagged-only-toggle"
          checked={flaggedOnly}
          onChange={(e) => onToggleFlagged(e.target.checked)}
          className="h-3 w-3 accent-orange-500"
        />
        Show flagged only ({flaggedCount})
      </label>
    </div>
  );
}

export function CueBadges({ seg }) {
  const flags = new Set(seg.qc_flags || []);
  const cps = liveCps(seg);
  const chips = [];
  if (flags.has("UNSPLITTABLE_SPAN")) {
    chips.push({ id: `unsplittable-${seg.index}`, label: "UNSPLITTABLE_SPAN", tone: "orange",
      tip: "This cue is too fast or too short to read, but its source words cannot be split further or merged with a neighbour without exceeding 7 s or crossing a sentence. Timing was left honest instead of being invented." });
  }
  if (cps !== null && cps > HARD_MAX_CPS) {
    chips.push({ id: `cps-${seg.index}`, label: `CPS ${cps.toFixed(1)}`, tone: "red",
      tip: `Reading speed is ${cps.toFixed(1)} characters/second — above the hard maximum of ${HARD_MAX_CPS} (target 12–17). Shorten the text; the timing comes from the spoken source words.` });
  }
  if (flags.has("BOUNDARY_SHIFT")) {
    chips.push({ id: `shift-${seg.index}`, label: "BOUNDARY_SHIFT", tone: "yellow",
      tip: "The span boundary was moved by one adjacent source word at the translator's request for better grammar. The move was validated: no coverage gap/overlap, contiguous span, CPS and duration limits respected." });
  }
  if ([...flags].some((f) => TECH_FLAGS.has(f))) {
    chips.push({ id: `tech-${seg.index}`, label: "TECHNICAL_MISMATCH", tone: "red",
      tip: "A protected number/spec value (e.g. 40W, 120Hz, ₹11 lakh) did not survive translation exactly and was restored by fallback. Check the value against the source." });
  }
  if (flags.has("outro_midvideo")) {
    chips.push({ id: `anomaly-${seg.index}`, label: "ANOMALY", tone: "purple",
      tip: "Outro language detected mid-video" });
  }
  if (Number.isInteger(seg.source_word_start_index) && Number.isInteger(seg.source_word_end_index)) {
    chips.push({ id: `span-${seg.index}`, label: `w${seg.source_word_start_index}–${seg.source_word_end_index}`, tone: "grey",
      tip: `Source words ${seg.source_word_start_index}–${seg.source_word_end_index}: this cue starts at the first word's timestamp and ends at the last word's (plus ≤ 300 ms lead-out).` });
  }
  if (!chips.length) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <span data-testid={`cue-badges-${seg.index}`} className="inline-flex items-center gap-1 flex-wrap">
        {chips.map((c) => <Chip key={c.id} {...c} />)}
      </span>
    </TooltipProvider>
  );
}
